import { FC, ReactNode, useState } from 'react';
import clsx from 'clsx';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  Divider,
  TextField,
} from '@mui/material';
import { FilterList, Close, ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { IDropdownResponse } from '../../models/util';
import { STATUS } from './EmployeeProfileManagementList';

interface IEmployeeProfileFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  roles: IDropdownResponse[];
  selectedRole: string;
  setSelectedRole: (val: string) => void;
  statuses: IDropdownResponse[];
  selectedStatus: string;
  setSelectedStatus: (val: string) => void;
  setSearchValue: (val: string) => void;
  searchValue: string;
  handleSearch: (val: string) => void;
  hasAppliedFilters: boolean;
  setHasAppliedFilters: (val: boolean) => void;
  extraActions?: ReactNode;
}

export const EmployeeProfileFIlters: FC<IEmployeeProfileFilters> = ({
  isLoading,
  applyFilters,
  setSearchValue,
  searchValue,
  hasAppliedFilters,
  setHasAppliedFilters,
  roles,
  selectedRole,
  setSelectedRole,
  statuses,
  selectedStatus,
  setSelectedStatus,
  extraActions,
}) => {
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);
  const classes = useStyles({ isMobileFilterButtonOpen });

  const noFilters = !selectedRole && !searchValue && !selectedStatus;
  return (
    <>
      <Grid container spacing={1} alignItems="center" className={classes.searchWrapper}></Grid>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper} sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Grid item xs={12} sm={6} md>
          <TextField
            fullWidth
            variant="outlined"
            autoComplete="nope"
            label="Name"
            name="Name"
            classes={{ root: classes.searchRoot }}
            value={searchValue}
            size="small"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                setHasAppliedFilters(true);
                applyFilters();
              }
            }}
            onChange={e => {
              setSearchValue(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3} md>
          <FormControl
            classes={{ root: classes.formControlRoot }}
            fullWidth
            variant="outlined"
            size="small"
          >
            <InputLabel htmlFor="role" id="roleLabel" className={classes.ellipsis}>
              Role
            </InputLabel>
            <Select
              disabled={isLoading}
              name="role"
              labelId="roleLabel"
              id="role"
              value={selectedRole}
              onChange={({ target: { value } }) => setSelectedRole(value)}
            >
              {roles &&
                roles.map((role, index) => {
                  return (
                    <MenuItem key={`${index}`} value={role.value}>
                      {role.description}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3} md>
          <FormControl
            classes={{ root: classes.formControlRoot }}
            fullWidth
            variant="outlined"
            size="small"
          >
            <InputLabel htmlFor="status" id="statusLabel" className={classes.ellipsis}>
              Status
            </InputLabel>
            <Select
              disabled={isLoading}
              name="status"
              labelId="statusLabel"
              id="status"
              value={selectedStatus}
              onChange={({ target: { value } }) => setSelectedStatus(value)}
            >
              {statuses &&
                statuses.map((status, index) => {
                  return (
                    <MenuItem key={`${index}`} value={status.value}>
                      {status.description}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md="auto" sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-start', gap: 1 }}>
          <Button
            color="secondary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              if (noFilters) {
                setHasAppliedFilters(false);
                applyFilters();
              } else {
                setHasAppliedFilters(true);
                applyFilters();
              }
            }}
            sx={{ flexShrink: 0 }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              color="inherit"
              disabled={isLoading}
              startIcon={<Close />}
              onClick={() => {
                setSearchValue('');
                setHasAppliedFilters(false);
                setSelectedRole('');
                setSelectedStatus(STATUS.ACTIVE);
                applyFilters(true);
              }}
              sx={{ flexShrink: 0 }}
            >
              Reset
            </Button>
          )}
          {extraActions}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    '@media only screen and (min-width: 900px)': {
      display: 'none',
    },
  },
  button: {
    marginTop: 4,
    marginBottom: 4,
    height: 40,
    textTransform: 'capitalize',
    width: '100%',

    '@media only screen and (min-width: 900px)': {
      width: 'auto',
    },
  },
  resetButton: {
    '@media only screen and (min-width: 900px)': {
      marginLeft: '1rem',
    },
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex',
        }
      : {
          display: 'none',
          marginBottom: '1rem',
          '@media only screen and (min-width: 900px)': {
            display: 'flex',
          },
        },
  divider: {
    display: 'block',
    marginBottom: '1rem',
    '@media only screen and (min-width: 900px)': {
      display: 'none',
    },
  },
  gridItem: {
    padding: `3px 8px !important`,
    '@media only screen and (min-width: 900px)': {
      padding: `8px !important`,
    },
  },
  searchIcon: {
    cursor: 'pointer',
    color: '#9e9e9e',
  },
  ellipsis: {
    backgroundColor: 'white',
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible',
    },
  },
  formControlRoot: {
    marginTop: 0,
  },
  textFieldRoot: {
    marginBottom: 0,
  },
  searchWrapper: {
    marginBottom: '1rem',
    '@media only screen and (min-width: 900px)': {
      display: 'flex',
      marginBottom: '1rem',
    },
  },
  clearSearchButton: {
    '@media (min-width: 408px)': {
      marginTop: 3,
      marginLeft: '1rem',
    },
  },
  searchButton: {
    minWidth: '8rem',
  },
  searchRoot: {
    marginTop: 0,
  },
}));
